import classNames from "classnames";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const PASSCODE_LENGTH = 6;
const LAST_INDEX = PASSCODE_LENGTH - 1;
const DIVIDER_BEFORE_INDEX = 3;
const VALID_CHARACTER = /^[0-9A-Za-z]$/;
const BOX_INDEXES = Array.from(
  { length: PASSCODE_LENGTH },
  (_, index) => index,
);
const EMPTY_PASSCODE = BOX_INDEXES.map(() => "");

interface PasscodeProps extends React.ComponentProps<"input"> {
  name: string;
  label?: string;
  required?: boolean;
  error?: string;
}

const toValidCharacters = (text: string) =>
  text
    .split("")
    .filter((character) => VALID_CHARACTER.test(character))
    .map((character) => character.toUpperCase())
    .slice(0, PASSCODE_LENGTH);

const insertedText = (previous: string, next: string) => {
  if (!previous) return next;
  if (next.startsWith(previous)) return next.slice(previous.length);
  if (next.endsWith(previous)) return next.slice(0, -previous.length);
  return next;
};

const isTypedCharacter = (event: React.KeyboardEvent<HTMLInputElement>) =>
  event.key.length === 1 &&
  !event.ctrlKey &&
  !event.metaKey &&
  !event.altKey &&
  !event.nativeEvent.isComposing;

function Passcode({
  label,
  value = "",
  onChange = () => {},
  error = "",
}: PasscodeProps) {
  const { t } = useTranslation();
  const [passcode, setPasscode] = useState(EMPTY_PASSCODE);
  const [incomplete, setIncomplete] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const errorId = useId();
  const lastSyncedValue = useRef<string | null>(null);

  const message =
    error ||
    (incomplete ? t("passcodeIncomplete", { count: PASSCODE_LENGTH }) : "");
  const hasMessage = !!message;

  const inputClass = classNames(
    "rounded border",
    "w-target h-target",
    "text-center",
    "focus:border-focus focus:outline-none focus-visible:ring-2 focus-visible:ring-focus",
    {
      "border-default": !error,
      "border-danger": !!error,
    },
  );

  const focusBox = (index: number) => refs.current[index]?.focus();

  const update = (next: string[]) => {
    setPasscode(next);
    setIncomplete(false);
  };

  const writeBox = (index: number, character: string) => {
    const next = [...passcode];
    next[index] = character;
    update(next);
  };

  const fillFromFirstBox = (text: string) => {
    const characters = toValidCharacters(text);
    if (characters.length === 0) return;
    update(BOX_INDEXES.map((index) => characters[index] ?? ""));
    focusBox(Math.min(characters.length, LAST_INDEX));
  };

  const onChangeBox = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const next = event.target.value;
    if (next === "") {
      writeBox(index, "");
      return;
    }
    if (next.length - passcode[index].length > 1) {
      fillFromFirstBox(next);
      return;
    }
    typeCharacter(index, insertedText(passcode[index], next));
  };

  const typeCharacter = (index: number, character: string) => {
    if (!VALID_CHARACTER.test(character)) return;
    writeBox(index, character.toUpperCase());
    if (index < LAST_INDEX) focusBox(index + 1);
  };

  const onBeforeInputBox = (
    event: React.InputEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.data?.length !== 1) return;
    event.preventDefault();
    typeCharacter(index, event.data);
  };

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fillFromFirstBox(event.clipboardData.getData("text"));
  };

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (isTypedCharacter(event)) {
      event.preventDefault();
      typeCharacter(index, event.key);
      return;
    }
    switch (event.key) {
      case "Backspace":
        event.preventDefault();
        if (passcode[index]) {
          writeBox(index, "");
        } else if (index > 0) {
          writeBox(index - 1, "");
          focusBox(index - 1);
        }
        return;
      case "Delete":
        event.preventDefault();
        writeBox(index, "");
        return;
      case "ArrowLeft":
        event.preventDefault();
        if (index > 0) focusBox(index - 1);
        return;
      case "ArrowRight":
        event.preventDefault();
        if (index < LAST_INDEX) focusBox(index + 1);
        return;
      case "ArrowUp":
      case "ArrowDown":
        event.preventDefault();
        return;
      case "Home":
        event.preventDefault();
        focusBox(0);
        return;
      case "End":
        event.preventDefault();
        focusBox(LAST_INDEX);
        return;
      case "Enter": {
        const firstEmpty = passcode.findIndex((character) => !character);
        if (firstEmpty === -1) return;
        event.preventDefault();
        setIncomplete(true);
        focusBox(firstEmpty);
        return;
      }
    }
  };

  useEffect(() => {
    const incoming = String(value);
    if (incoming === lastSyncedValue.current) return;
    lastSyncedValue.current = incoming;
    if (incoming === passcode.join("")) return;
    const characters = toValidCharacters(incoming);
    setPasscode(BOX_INDEXES.map((index) => characters[index] ?? ""));
  }, [value, passcode]);

  useEffect(() => {
    onChange({
      target: { value: passcode.join("") },
    } as React.ChangeEvent<HTMLInputElement>);
  }, [passcode, onChange]);

  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">
        {label || t("passcodeLegend", { count: PASSCODE_LENGTH })}
      </legend>
      <div className="flex w-full justify-between items-center">
        {BOX_INDEXES.map((index) => (
          <Fragment key={index}>
            {index === DIVIDER_BEFORE_INDEX && (
              <div className="w-6 h-0 border-t border-strong"></div>
            )}
            <input
              type="text"
              ref={(node) => {
                refs.current[index] = node;
              }}
              className={inputClass}
              value={passcode[index]}
              maxLength={index === 0 ? undefined : 1}
              autoComplete={index === 0 ? "one-time-code" : undefined}
              aria-label={t("passcodeCharacter", {
                index: index + 1,
                count: PASSCODE_LENGTH,
              })}
              aria-invalid={hasMessage || undefined}
              aria-describedby={hasMessage ? errorId : undefined}
              onFocus={(event) => event.target.select()}
              onChange={(event) => onChangeBox(event, index)}
              onBeforeInput={(event) => onBeforeInputBox(event, index)}
              onPaste={onPaste}
              onKeyDown={(event) => onKeyDown(event, index)}
            />
          </Fragment>
        ))}
      </div>
      <p
        id={errorId}
        role="alert"
        className="text-xs ml-1 mt-1 text-danger font-comfortaa"
      >
        {message}
      </p>
    </fieldset>
  );
}

export default Passcode;
